import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Call as CallIcon,
  BatchPrediction as BatchesIcon,
  People as PeopleIcon,
  Feedback as FeedbackIcon,
  Science as TestIcon,
  PhoneForwarded as OutboundIcon,
  BarChart as AnalyticsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Plotly from 'plotly.js-dist-min';
import createPlotlyComponent from 'react-plotly.js/factory';
import { useNavigate, useLocation } from 'react-router-dom';
import { analyticsService, authService, organizationService } from '../utils/api';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';

const Plot = createPlotlyComponent(Plotly);
function Analytics() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cards, setCards] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [dateRange, setDateRange] = useState('last30');

  const userRole = authService.getUserRole();
  const canViewCosts = userRole === 'super_admin' || userRole === 'admin';

  const drawerWidth = 240;

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7', label: 'Last 7 days' },
    { value: 'last30', label: 'Last 30 days' },
    { value: 'thisMonth', label: 'This month' },
    { value: 'lastMonth', label: 'Last month' },
  ];

  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      // Pass date range as query parameter
      const data = await analyticsService.getAnalytics(dateRange);
      setCards(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      const message = err?.detail || err?.message || 'Failed to load analytics.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Refetch when date range changes
  useEffect(() => {
    if (currentOrganization) {
      fetchAnalytics();
    }
  }, [dateRange]);

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...((canViewCosts || userRole === 'moderator') && currentOrganization?.call_type !== 'inboundPhoneCall'
      ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }]
      : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall'
      ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }]
      : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator'
      ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }]
      : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const scoreCards = cards.filter((card) => card.type === 'score_card');
  const graphCards = cards.filter((card) => card.type === 'graph');

  // Compact KPI card for top section (Zoho-style)
  const renderCompactKPI = (card) => (
    <Box
      key={`kpi-${card.index || card.name}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        bgcolor: 'white',
        borderRadius: 2,
        border: '1px solid rgba(102, 117, 140, 0.08)',
        minWidth: 180,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 1,
          bgcolor: 'rgba(67, 97, 238, 0.08)',
        }}
      >
        <AnalyticsIcon sx={{ fontSize: 20, color: 'primary.main' }} />
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
          {card.value ?? '-'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {card.name}
        </Typography>
      </Box>
    </Box>
  );

  const renderGraphCard = (card) => {
    // Strip heavy template to avoid Plotly errors and reduce payload
    const layout = { ...(card.graph?.layout || {}) };
    if (layout.template) {
      delete layout.template;
    }

    const isSunburst =
      (card.graph?.data && card.graph.data[0]?.type === 'sunburst') ||
      (card.name || '').toLowerCase().includes('sunburst');

    // Prepare data with styling overrides for sunburst
    const data = (card.graph?.data || []).map((trace) => {
      if (!isSunburst) return trace;
      return {
        ...trace,
        // Let backend handle all styling - don't override
      };
    });

    if (isSunburst) {
      layout.margin = { t: 40, l: 40, r: 40, b: 40 };
      layout.paper_bgcolor = 'rgba(248, 250, 252, 0.4)';
      layout.plot_bgcolor = 'transparent';
      layout.width = 550;
      layout.height = 550;
      layout.font = { family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', size: 11 };
    }

    return (
      <Grid item xs={12} md={6} key={`graph-${card.index || card.name}`}>
        <Card
          sx={{
            border: '1px solid rgba(102, 117, 140, 0.08)',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'white',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.95rem' }}>
                  {card.name}
                </Typography>
                {card.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {card.description}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ 
              width: '100%', 
              height: isSunburst ? 600 : 380, 
              display: 'flex', 
              justifyContent: isSunburst ? 'center' : 'stretch',
              alignItems: 'center',
            }}>
              <Plot
                data={data}
                layout={{
                  ...layout,
                  autosize: !isSunburst,
                  height: isSunburst ? layout.height : 340,
                  // Preserve backend margins for proper legend spacing
                  margin: isSunburst ? layout.margin : { ...(layout.margin || {}), t: layout.margin?.t || 20, b: layout.margin?.b || 50 },
                  paper_bgcolor: 'transparent',
                  plot_bgcolor: 'transparent',
                }}
                config={{ responsive: true, displayModeBar: false }}
                style={{ width: isSunburst ? '550px' : '100%', height: isSunburst ? '550px' : '100%' }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box
          sx={{
            width: drawerWidth,
            height: '100%',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
            borderRight: '1px solid rgba(102, 117, 140, 0.12)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: '12px',
              pb: '12px',
              px: 2,
              borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
              background: '#2541b2',
              color: 'white',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Box component="img" src="/telly_logo.png" alt="TellyFlow" sx={{ height: '32px', objectFit: 'contain' }} />
              <Box sx={{ width: '1px', height: '24px', backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
              <Box
                sx={{
                  width: '60px',
                  height: '40px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {COMPANY_LOGO && (
                  <Box
                    component="img"
                    src={COMPANY_LOGO}
                    alt={`${COMPANY_NAME || 'Company'} Logo`}
                    sx={{ height: '60px', objectFit: 'cover', objectPosition: 'center' }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, py: 2 }}>
            <List sx={{ px: 2 }}>
              {drawerItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem
                    button
                    key={item.text}
                    onClick={() => navigate(item.path)}
                    sx={{
                      mb: 0.5,
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                      border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                      '&:hover': {
                        backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                        transform: 'translateX(2px)',
                        boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)',
                      },
                      '&:active': {
                        transform: 'translateX(1px)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive ? 'primary.main' : 'text.secondary',
                        transition: 'color 0.2s ease-in-out',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.875rem',
                        color: isActive ? 'primary.main' : 'text.primary',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    />
                    {isActive && (
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          ml: 1,
                        }}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>

          <Divider />

          <Box
            sx={{
              p: 2,
              borderTop: '1px solid rgba(102, 117, 140, 0.08)',
              background: 'rgba(102, 117, 140, 0.02)',
            }}
          >
            <ListItem
              button
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(239, 71, 111, 0.08)',
                  transform: 'translateX(2px)',
                  boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)',
                },
                '&:active': {
                  transform: 'translateX(1px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: 'text.secondary',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                }}
              />
            </ListItem>
          </Box>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen && !isMobile ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid rgba(102, 117, 140, 0.08)', bgcolor: 'white' }}>
          <Toolbar sx={{ py: 0.5, minHeight: 56 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{
                mr: 2,
                '&:hover': {
                  backgroundColor: 'rgba(67, 97, 238, 0.08)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}>
              Analytics Dashboard
            </Typography>
            {currentOrganization && (
              <Chip
                icon={<BusinessIcon />}
                label={currentOrganization.name}
                onClick={handleSwitchOrganization}
                sx={{
                  borderColor: 'rgba(67, 97, 238, 0.2)',
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: 'rgba(67, 97, 238, 0.08)',
                  },
                }}
                variant="outlined"
              />
            )}
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ mt: 0, mb: 4, px: { xs: 2, sm: 3 } }}>
          {/* Top Section: Date Filter and Compact KPIs */}
          <Box sx={{ mb: 3, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Overview
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    startAdornment={<CalendarIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />}
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(102, 117, 140, 0.2)',
                      },
                    }}
                  >
                    {dateRangeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={fetchAnalytics}
                  sx={{
                    bgcolor: 'white',
                    border: '1px solid rgba(102, 117, 140, 0.2)',
                    '&:hover': {
                      bgcolor: 'rgba(67, 97, 238, 0.08)',
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            {error && (
              <Card sx={{ mb: 2, border: '1px solid rgba(239, 71, 111, 0.2)', backgroundColor: 'rgba(239, 71, 111, 0.04)' }}>
                <CardContent>
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <>
                {/* Compact KPI Row */}
                {scoreCards.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      overflowX: 'auto',
                      pb: 1,
                      mb: 3,
                      '&::-webkit-scrollbar': {
                        height: 6,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(102, 117, 140, 0.2)',
                        borderRadius: 3,
                      },
                    }}
                  >
                    {scoreCards.slice(0, 5).map((card) => renderCompactKPI(card))}
                  </Box>
                )}

                {/* Two Charts Per Row (Zoho-style) */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {graphCards.map((card) => renderGraphCard(card))}
                </Grid>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Analytics;
